import { CHILDREN_QUALIFICATION_CODES } from '../../shared/constants/qualification-code';
import { CHILDREN_QUALIFICATION_NAMES } from '../../shared/constants/qualification-name';
import { QuotaPayload } from '../../shared/interfaces/quota.interface';

export interface QuotaMatch {
  filter(quota: QuotaPayload): boolean;
}

export class SimpleQuotaMatch implements QuotaMatch {
  filter(quota: QuotaPayload): boolean {
    return quota.criteria.length === 1;
  }
}

export class DeselectQuotaMatch implements QuotaMatch {
  constructor(private readonly _deselected: number[]) {}

  filter(quota: QuotaPayload): boolean {
    return quota.criteria.some((criteria) =>
      this._deselected.includes(criteria.qualification_code)
    );
  }
}

export class RemainQuotaMatch implements QuotaMatch {
  constructor(private readonly _ids: number[]) {}

  filter(quota: QuotaPayload): boolean {
    return quota.criteria.every(
      (criteria) => !this._ids.includes(criteria.qualification_code)
    );
  }
}

export class ChildrenQuotaMatch implements QuotaMatch {
  filter(quota: QuotaPayload): boolean {
    return (
      CHILDREN_QUALIFICATION_CODES.includes(
        quota.criteria[0].qualification_code
      ) ||
      CHILDREN_QUALIFICATION_NAMES.includes(
        quota.criteria[0].qualification_name ?? ''
      )
    );
  }
}

export class CategoryQuotaMatch implements QuotaMatch {
  constructor(private readonly _category: string) {}

  filter(quota: QuotaPayload): boolean {
    return quota.quota_category === this._category;
  }
}

export interface QuotaFilterMatch {
  quotas(match: QuotaMatch): QuotaPayload[];
}

export interface QuotaFilterUnmatched {
  unmatched(): QuotaFilterMatch;
}

export interface QuotaFilterDelegate {
  filter(match: QuotaMatch, quota: QuotaPayload): boolean;
}

export class QuotaEqual implements QuotaFilterDelegate {
  filter(match: QuotaMatch, quota: QuotaPayload): boolean {
    return match.filter(quota);
  }
}

export class QuotaDifferent implements QuotaFilterDelegate {
  filter(match: QuotaMatch, quota: QuotaPayload): boolean {
    return !match.filter(quota);
  }
}

export class QuotaFilter implements QuotaFilterMatch, QuotaFilterUnmatched {
  constructor(
    private readonly _quotas: QuotaPayload[],
    private readonly _delegate = new QuotaEqual()
  ) {}

  quotas(match: QuotaMatch): QuotaPayload[] {
    return this._quotas.filter((quota) => this._delegate.filter(match, quota));
  }

  unmatched(): QuotaFilterMatch {
    return new QuotaFilter(this._quotas, new QuotaDifferent());
  }
}

export class QuotaDistinct {
  constructor(
    private readonly _quotas: QuotaPayload[],
    private readonly _others: QuotaPayload[]
  ) {}

  by(match: QuotaMatch): QuotaDistinct {
    const quotas = this._quotas.filter((quota) => match.filter(quota));
    return new QuotaDistinct(quotas, this._others);
  }

  unique(): QuotaPayload[] {
    const existents = new Set<number>(
      this._quotas.map((quota) => quota.criteria[0].qualification_code)
    );
    const match = new RemainQuotaMatch(Array.from(existents.values()));
    const missing = this._others.filter((quota) => match.filter(quota));

    return [...this._quotas, ...missing];
  }
}
